import { useEffect, useState } from 'react';
import api, { formatApiError, API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Check, X, ClipboardCheck, FileText, Mail, Eye, Sparkles, ShieldCheck, Send, Settings2 } from 'lucide-react';
import { previewPDF } from '../lib/pdf';

function fileUrl(fid) {
  const token = localStorage.getItem('hrms_token');
  return `${API}/files/${fid}?auth=${encodeURIComponent(token)}`;
}

const personalFields = [
  { k: 'date_of_birth', label: 'Date of birth' },
  { k: 'gender', label: 'Gender' },
  { k: 'marital_status', label: 'Marital status' },
  { k: 'emergency_contact_name', label: 'Emergency contact' },
  { k: 'emergency_contact_phone', label: 'Emergency phone' },
  { k: 'address', label: 'Address' },
];
const idFields = [
  { k: 'aadhar_number', label: 'Aadhar #' },
  { k: 'pan_number', label: 'PAN #' },
];
const eduFields = [
  { k: 'undergrad_institute', label: 'Undergrad institute' },
  { k: 'postgrad_institute', label: 'Postgrad institute' },
];

export default function Approvals() {
  const [list, setList] = useState([]);
  const [bgcCleared, setBgcCleared] = useState([]);  // ready for work email
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [decisionOpen, setDecisionOpen] = useState(null);
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get('/onboarding/pending'),
        api.get('/employees'),
      ]);
      setList(a.data);
      setBgcCleared(b.data.filter(u => u.onboarding_status === 'bgc_cleared'));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const decide = async (emp, action) => {
    try {
      await api.post(`/onboarding/${emp.id}/decision?action=${action}`, { note });
      toast.success(action === 'approve' ? 'BGC approved' : 'Returned to candidate');
      setDecisionOpen(null); setNote(''); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // -------- Revoke offer + Request more info --------
  const [revokeEmp, setRevokeEmp] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [moreInfoEmp, setMoreInfoEmp] = useState(null);
  const [moreInfoNote, setMoreInfoNote] = useState('');
  const [moreInfoBusy, setMoreInfoBusy] = useState(false);

  const revokeOffer = async () => {
    if (!revokeEmp || !revokeReason.trim()) { toast.error('A reason is required'); return; }
    setRevokeBusy(true);
    try {
      await api.post(`/employees/${revokeEmp.id}/revoke-offer`, { reason: revokeReason });
      toast.success(`Offer revoked for ${revokeEmp.name}. Account deleted.`);
      setRevokeEmp(null); setRevokeReason(''); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRevokeBusy(false); }
  };

  const requestMoreInfo = async () => {
    if (!moreInfoEmp || !moreInfoNote.trim()) { toast.error('Please describe what info is needed'); return; }
    setMoreInfoBusy(true);
    try {
      await api.post(`/onboarding/${moreInfoEmp.id}/request-more-info`, { note: moreInfoNote });
      toast.success('Candidate notified — they can now re-upload documents.');
      setMoreInfoEmp(null); setMoreInfoNote(''); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMoreInfoBusy(false); }
  };

  const [activateEmp, setActivateEmp] = useState(null);
  const [activateForm, setActivateForm] = useState({ mode: 'auto', work_email: '', password: '' });
  const [activateBusy, setActivateBusy] = useState(false);

  // -------- Offer PDF send + Letter signature settings --------
  const [offerEmp, setOfferEmp] = useState(null);   // employee for which we want to send Offer PDF
  const [offerBusy, setOfferBusy] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigForm, setSigForm] = useState({
    signer_name: '', signer_designation: '', contact_email: '',
    contact_phone: '', contact_address: '', footer_note: '',
  });
  const [sigBusy, setSigBusy] = useState(false);

  const loadSig = async () => {
    try {
      const { data } = await api.get('/letter-settings');
      setSigForm({
        signer_name: data.signer_name || '',
        signer_designation: data.signer_designation || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        contact_address: data.contact_address || '',
        footer_note: data.footer_note || '',
      });
    } catch (e) { /* ignore */ }
  };

  const openSig = async () => { await loadSig(); setSigOpen(true); };
  const saveSig = async () => {
    setSigBusy(true);
    try {
      await api.put('/letter-settings', sigForm);
      toast.success('Signature saved — will appear on all letters you issue');
      setSigOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSigBusy(false); }
  };

  const previewOffer = async (emp) => {
    try { await previewPDF(`${API}/employees/${emp.id}/offer-letter.pdf`); }
    catch (e) { toast.error('Preview failed: ' + (e.message || 'unknown')); }
  };

  const sendOffer = async () => {
    if (!offerEmp) return;
    setOfferBusy(true);
    try {
      await api.post(`/employees/${offerEmp.id}/send-offer-pdf`);
      toast.success(`Offer letter PDF emailed to ${offerEmp.personal_email || offerEmp.email}`);
      setOfferEmp(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOfferBusy(false); }
  };

  const startActivate = async (emp) => {
    setActivateEmp(emp);
    setActivateForm({ mode: 'auto', work_email: '', password: '' });
    if (!emp.manual_creation) {
      try {
        const { data } = await api.post(`/employees/${emp.id}/preview-work-email`);
        setActivateForm(f => ({ ...f, work_email: data.work_email }));
      } catch { /* empty */ }
    }
  };

  const markVerified = async (emp) => {
    if (!window.confirm(`Activate ${emp.name} without issuing a work email?\n\nThey'll get full HRMS access via their personal-email login. You can issue the work email separately later (e.g. when IT provisions the mailbox).`)) return;
    try {
      const { data } = await api.post(`/employees/${emp.id}/mark-verified`);
      toast.success(`${emp.name} is now active (employee ID: ${data.employee_id}). Work email pending.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Could not activate'); }
  };

  const confirmActivate = async () => {
    if (!activateEmp) return;
    const isManualUser = !!activateEmp.manual_creation;
    setActivateBusy(true);
    try {
      let body = null;
      if (!isManualUser && activateForm.mode === 'manual') {
        if (!activateForm.work_email.trim()) { setActivateBusy(false); return toast.error('Enter the work email'); }
        if (!activateForm.password.trim() || activateForm.password.length < 8) { setActivateBusy(false); return toast.error('Password must be at least 8 characters'); }
        body = { work_email: activateForm.work_email.trim(), password: activateForm.password };
      }
      const { data } = await api.post(`/employees/${activateEmp.id}/create-work-email`, body || {});
      toast.success(data.manual
        ? `${activateEmp.name} now has full access (existing credentials kept)`
        : `Activated: ${data.work_email}`);
      setActivateEmp(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActivateBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="approvals-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">HR approvals</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">BGC & Activation queue</h1>
          <p className="text-slate-500 mt-1">{list.length} BGC pending · {bgcCleared.length} ready for work email</p>
        </div>
        <Button variant="outline" onClick={openSig} data-testid="letter-signature-btn" className="rounded-full">
          <Settings2 className="w-4 h-4 mr-1.5" /> My letter signature
        </Button>
      </div>

      {/* BGC review section */}
      <section>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Background-check review</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {list.map(emp => (
            <div key={emp.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`approval-${emp.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-[#3D4A8E] text-white flex items-center justify-center font-heading text-lg font-bold flex-shrink-0">
                    {emp.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{emp.name}</div>
                    <div className="text-xs text-slate-500 truncate">{emp.title || '—'} · {emp.department}</div>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">BGC review</span>
              </div>
              <div className="mt-3 text-xs text-slate-500 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {emp.email}</div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => { setActive(emp); setOpen(true); }}
                        data-testid={`view-${emp.id}`} className="rounded-full">
                  <Eye className="w-4 h-4 mr-1.5" /> View documents
                </Button>
                <Button size="sm" onClick={() => setDecisionOpen({ emp, action: 'approve' })} data-testid={`approve-${emp.id}`}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Check className="w-4 h-4 mr-1" /> Approve BGC
                </Button>
                <Button size="sm" variant="outline" onClick={() => setMoreInfoEmp(emp)} data-testid={`more-info-${emp.id}`}
                        className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50">
                  Request more info
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDecisionOpen({ emp, action: 'reject' })} data-testid={`reject-${emp.id}`}
                        className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                  <X className="w-4 h-4 mr-1" /> Return
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRevokeEmp(emp)} data-testid={`revoke-${emp.id}`}
                        className="rounded-full border-red-300 text-red-700 hover:bg-red-100">
                  Revoke offer
                </Button>
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400 flex flex-col items-center gap-3" data-testid="no-bgc">
              <ClipboardCheck className="w-10 h-10 text-slate-300" />
              <div>No BGC reviews pending.</div>
            </div>
          )}
        </div>
      </section>

      {/* BGC cleared - ready for activation */}
      {bgcCleared.length > 0 && (
        <section>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Ready for work email & activation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {bgcCleared.map(emp => (
              <div key={emp.id} className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-2xl p-5" data-testid={`activate-${emp.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-heading text-lg font-bold flex-shrink-0">
                      {emp.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{emp.name}</div>
                      <div className="text-xs text-slate-500 truncate">{emp.title || '—'}</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">BGC cleared</span>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  {emp.manual_creation
                    ? <>Work email: <b>{emp.email}</b> · <span className="text-emerald-700 font-bold">existing credentials</span></>
                    : <>Personal email: <b>{emp.personal_email || emp.email}</b></>}
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  {!emp.manual_creation && (
                    <Button size="sm" onClick={() => markVerified(emp)} data-testid={`mark-verified-btn-${emp.id}`}
                            variant="outline" className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      Activate account only (no work email yet)
                    </Button>
                  )}
                  <Button size="sm" onClick={() => startActivate(emp)} data-testid={`activate-btn-${emp.id}`}
                          className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white">
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    {emp.manual_creation ? 'Grant full access' : 'Issue work email & send credentials'}
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => previewOffer(emp)}
                          data-testid={`preview-offer-${emp.id}`} className="rounded-full text-xs">
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview offer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setOfferEmp(emp)}
                          data-testid={`send-offer-${emp.id}`} className="rounded-full text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Send offer PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* View documents dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="approval-details" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5" /> {active.name} — BGC documents
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <div className="bg-slate-50 rounded-xl p-4 text-sm grid grid-cols-2 gap-x-6 gap-y-2">
                  <div><span className="text-slate-500">Employee ID:</span> <b data-testid="bgc-emp-id">{active.employee_id || '—'}</b></div>
                  <div><span className="text-slate-500">Personal email:</span> <b>{active.personal_email || active.email}</b></div>
                  <div><span className="text-slate-500">Title:</span> <b>{active.title || '—'}</b></div>
                  <div><span className="text-slate-500">Grade:</span> <b>{active.grade || '—'}</b></div>
                  <div><span className="text-slate-500">Department:</span> <b>{active.department || '—'}</b></div>
                  <div><span className="text-slate-500">CTC:</span> <b>{active.annual_ctc ? `₹${Number(active.annual_ctc).toLocaleString('en-IN')}` : '—'}</b></div>
                </div>

                <Group title="Personal" testId="bgc-personal">
                  <Grid>{personalFields.map(f => <Cell key={f.k} label={f.label} value={active.onboarding?.[f.k]} />)}</Grid>
                </Group>

                <Group title="Identity (KYC)" testId="bgc-identity">
                  <Grid>{idFields.map(f => <Cell key={f.k} label={f.label} value={active.onboarding?.[f.k]} />)}</Grid>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                    {active.onboarding?.aadhar_file_id && <DocCard label="Aadhar card" fid={active.onboarding.aadhar_file_id} />}
                    {active.onboarding?.pan_file_id && <DocCard label="PAN card" fid={active.onboarding.pan_file_id} />}
                  </div>
                </Group>

                <Group title="Education" testId="bgc-education">
                  <Grid>{eduFields.map(f => <Cell key={f.k} label={f.label} value={active.onboarding?.[f.k]} />)}</Grid>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                    {active.onboarding?.undergrad_cert_file_id && <DocCard label="Undergrad certificate" fid={active.onboarding.undergrad_cert_file_id} />}
                    {active.onboarding?.postgrad_cert_file_id && <DocCard label="Postgrad certificate" fid={active.onboarding.postgrad_cert_file_id} />}
                  </div>
                </Group>

                <Group title={`Previous employers (${(active.onboarding?.previous_employers || []).length})`} testId="bgc-prev-employers">
                  {(active.onboarding?.previous_employers || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic">No previous employers declared.</div>
                  ) : (active.onboarding?.previous_employers || []).map((p, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl p-4" data-testid={`bgc-prev-emp-${i}`}>
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Employer #{i + 1}</div>
                      <Grid>
                        <Cell label="Company" value={p.company_name} />
                        <Cell label="Role" value={p.role} />
                        <Cell label="From" value={p.from_date} />
                        <Cell label="To" value={p.to_date} />
                        <Cell label="HR / Manager name" value={p.hr_name} />
                        <Cell label="HR / Manager email" value={p.hr_email} />
                        <Cell label="HR / Manager phone" value={p.hr_phone} />
                      </Grid>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                        {p.offer_letter_file_id && <DocCard label="Offer letter" fid={p.offer_letter_file_id} />}
                        {p.form16_file_id && <DocCard label="Form 16" fid={p.form16_file_id} />}
                        {(p.payslip_file_ids || []).map((fid, j) => <DocCard key={j} label={`Payslip ${j + 1}`} fid={fid} />)}
                      </div>
                    </div>
                  ))}
                </Group>

                <Group title={`Other certifications (${(active.onboarding?.certificate_file_ids || []).length})`} testId="bgc-certs">
                  {(active.onboarding?.certificate_file_ids || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic">No additional certificates uploaded.</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(active.onboarding?.certificate_file_ids || []).map((fid, i) => <DocCard key={i} label={`Certificate ${i + 1}`} fid={fid} />)}
                    </div>
                  )}
                </Group>

                <Group title="Criminal record disclosure" testId="bgc-criminal">
                  <div className={`rounded-xl p-3 text-sm ${active.onboarding?.has_criminal_record ? 'bg-red-50 border border-red-200 text-red-900' : 'bg-emerald-50 border border-emerald-200 text-emerald-900'}`}>
                    <b>{active.onboarding?.has_criminal_record ? '⚠ Disclosed' : '✓ None declared'}</b>
                    {active.onboarding?.has_criminal_record && active.onboarding?.criminal_record_details && (
                      <div className="text-xs text-slate-700 mt-2 whitespace-pre-wrap">{active.onboarding.criminal_record_details}</div>
                    )}
                  </div>
                </Group>

                <Group title="Consent" testId="bgc-consent">
                  <div className="text-sm flex items-center gap-2">
                    {active.onboarding?.terms_accepted ? (
                      <span className="text-emerald-700"><Check className="w-4 h-4 inline mr-1" /> Terms & background-check consent accepted</span>
                    ) : (
                      <span className="text-red-600"><X className="w-4 h-4 inline mr-1" /> Terms not yet accepted</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Submitted: {active.onboarding?.submitted_at ? new Date(active.onboarding.submitted_at).toLocaleString() : '—'}
                  </div>
                </Group>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDecisionOpen({ emp: active, action: 'reject' })} className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                  <X className="w-4 h-4 mr-1" /> Return for changes
                </Button>
                <Button onClick={() => setDecisionOpen({ emp: active, action: 'approve' })} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-4 h-4 mr-1" /> Approve BGC
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Activate-account dialog */}
      <Dialog open={!!activateEmp} onOpenChange={(o) => !o && setActivateEmp(null)}>
        <DialogContent className="max-w-md" data-testid="activate-dialog">
          {activateEmp && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {activateEmp.manual_creation ? 'Grant full access' : 'Activate work account'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {activateEmp.manual_creation ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
                    <b>{activateEmp.name}</b> already has a work email and password set up.
                    Confirming will simply flip their status to <b>verified</b> — credentials remain unchanged.
                  </div>
                ) : (
                  <>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm">
                      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Auto-generated work email</div>
                      <div className="font-mono text-[#3D4A8E] font-semibold" data-testid="activate-preview-email">{activateForm.work_email || 'loading…'}</div>
                      <div className="text-xs text-slate-500 mt-2">A random temporary password will be emailed to {activateEmp.personal_email || activateEmp.email}.</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid="toggle-manual-mode" type="button" onClick={() => setActivateForm(f => ({ ...f, mode: f.mode === 'auto' ? 'manual' : 'auto' }))}
                              className="text-xs text-[#3D4A8E] hover:underline">
                        {activateForm.mode === 'auto' ? 'I want to set the email & password manually instead →' : '← Use the auto-generated email instead'}
                      </button>
                    </div>
                    {activateForm.mode === 'manual' && (
                      <div className="space-y-3 bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="activate-manual-fields">
                        <div>
                          <Label className="text-xs font-semibold text-slate-700">Work email *</Label>
                          <Input data-testid="activate-manual-email" type="email" value={activateForm.work_email}
                                 onChange={(e) => setActivateForm({ ...activateForm, work_email: e.target.value })}
                                 placeholder={`${activateEmp.name?.split(' ')[0]?.toLowerCase()}@lanvaus.com`}
                                 className="rounded-xl mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-700">Initial password *</Label>
                          <Input data-testid="activate-manual-pw" type="text" value={activateForm.password}
                                 onChange={(e) => setActivateForm({ ...activateForm, password: e.target.value })}
                                 placeholder="At least 8 characters"
                                 className="rounded-xl mt-1 font-mono" />
                          <div className="text-xs text-slate-500 mt-1">HR-set passwords skip the forced-change prompt. Share them securely.</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActivateEmp(null)} className="rounded-full">Cancel</Button>
                <Button data-testid="confirm-activate" onClick={confirmActivate} disabled={activateBusy} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {activateBusy ? 'Activating…' : (activateEmp.manual_creation ? 'Confirm — grant full access' : (activateForm.mode === 'manual' ? 'Send custom credentials & activate' : 'Send credentials & activate'))}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision note dialog */}
      <Dialog open={!!decisionOpen} onOpenChange={(o) => !o && setDecisionOpen(null)}>
        <DialogContent className="max-w-md" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {decisionOpen?.action === 'approve' ? 'Approve background check' : 'Return documents'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Label className="text-xs font-semibold text-slate-600">
              {decisionOpen?.action === 'approve' ? 'Internal note (optional)' : 'Reason / required changes *'}
            </Label>
            <Textarea data-testid="decision-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionOpen(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="confirm-decision"
                    onClick={() => decide(decisionOpen.emp, decisionOpen.action)}
                    disabled={decisionOpen?.action === 'reject' && !note.trim()}
                    className={`rounded-full ${decisionOpen?.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} text-white`}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request more info dialog */}
      <Dialog open={!!moreInfoEmp} onOpenChange={(o) => !o && setMoreInfoEmp(null)}>
        <DialogContent className="max-w-md" data-testid="more-info-dialog">
          {moreInfoEmp && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Request more info from {moreInfoEmp.name}</DialogTitle>
                <DialogDescription>
                  We&apos;ll move the candidate back to <b>documents pending</b> and email them your note. They can then re-upload from the onboarding screen.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <Label className="text-xs font-semibold text-slate-600">What needs to be re-uploaded or clarified? *</Label>
                <Textarea data-testid="more-info-note" rows={4} value={moreInfoNote}
                          onChange={(e) => setMoreInfoNote(e.target.value)}
                          placeholder="e.g. PAN card is blurry, please re-upload a clearer scan; education certificate is missing year of completion." className="rounded-xl" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMoreInfoEmp(null)} className="rounded-full">Cancel</Button>
                <Button data-testid="confirm-more-info" onClick={requestMoreInfo} disabled={moreInfoBusy || !moreInfoNote.trim()}
                        className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">
                  {moreInfoBusy ? 'Sending…' : 'Notify candidate'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke offer dialog */}
      <Dialog open={!!revokeEmp} onOpenChange={(o) => !o && setRevokeEmp(null)}>
        <DialogContent className="max-w-md" data-testid="revoke-dialog">
          {revokeEmp && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-red-700">Revoke offer for {revokeEmp.name}?</DialogTitle>
                <DialogDescription className="text-red-600/80">
                  This will <b>permanently delete</b> the candidate&apos;s account and all uploaded documents. They&apos;ll receive a polite withdrawal email with the reason below. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <Label className="text-xs font-semibold text-slate-600">Reason for withdrawal *</Label>
                <Textarea data-testid="revoke-reason" rows={3} value={revokeReason}
                          onChange={(e) => setRevokeReason(e.target.value)}
                          placeholder="e.g. Failed reference verification, position no longer available, role re-scoped." className="rounded-xl" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRevokeEmp(null)} className="rounded-full">Cancel</Button>
                <Button data-testid="confirm-revoke" onClick={revokeOffer} disabled={revokeBusy || !revokeReason.trim()}
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                  {revokeBusy ? 'Revoking…' : 'Confirm revoke & delete account'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send offer PDF dialog */}
      <Dialog open={!!offerEmp} onOpenChange={(o) => !o && setOfferEmp(null)}>
        <DialogContent className="max-w-md" data-testid="send-offer-dialog">
          {offerEmp && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Send Offer Letter PDF</DialogTitle>
                <DialogDescription>
                  We&apos;ll generate the offer letter PDF (with your saved letter signature) and email it as an attachment to <b>{offerEmp.personal_email || offerEmp.email}</b>.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 mt-2">
                Tip — preview the PDF first to verify the candidate details and your signature block look correct. You can update your signature anytime via the <b>My letter signature</b> button at the top.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOfferEmp(null)} className="rounded-full">Cancel</Button>
                <Button data-testid="confirm-send-offer" onClick={sendOffer} disabled={offerBusy}
                        className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">
                  {offerBusy ? 'Sending…' : <><Send className="w-4 h-4 mr-1.5" /> Send PDF</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Letter signature settings dialog */}
      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="max-w-lg" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">My letter signature</DialogTitle>
            <DialogDescription>
              These details will appear in the signature block of every offer / relieving / experience / termination letter you issue. They&apos;re saved per HR user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Your name *</Label>
                <Input data-testid="sig-name" value={sigForm.signer_name}
                       onChange={(e) => setSigForm({ ...sigForm, signer_name: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Designation</Label>
                <Input data-testid="sig-designation" value={sigForm.signer_designation}
                       onChange={(e) => setSigForm({ ...sigForm, signer_designation: e.target.value })} placeholder="Human Resources" className="rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Contact email</Label>
              <Input data-testid="sig-email" type="email" value={sigForm.contact_email}
                     onChange={(e) => setSigForm({ ...sigForm, contact_email: e.target.value })} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Contact phone</Label>
              <Input data-testid="sig-phone" value={sigForm.contact_phone}
                     onChange={(e) => setSigForm({ ...sigForm, contact_phone: e.target.value })} placeholder="+91 ..." className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Address</Label>
              <Textarea data-testid="sig-address" rows={2} value={sigForm.contact_address}
                        onChange={(e) => setSigForm({ ...sigForm, contact_address: e.target.value })}
                        placeholder="Office address — appears under your signature" className="rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-signature" onClick={saveSig} disabled={sigBusy || !sigForm.signer_name?.trim()}
                    className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
              {sigBusy ? 'Saving…' : 'Save signature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocCard({ label, fid }) {
  return (
    <a href={fileUrl(fid)} target="_blank" rel="noreferrer" data-testid={`doccard-${fid}`}
       className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 hover:border-[#3D4A8E] transition-colors">
      <div className="w-9 h-9 rounded-lg bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center">
        <FileText className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="font-medium text-sm text-slate-900 truncate">{label}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Click to open</div>
      </div>
    </a>
  );
}

function Group({ title, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
);

const Cell = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="font-medium text-slate-900 break-words">{value || '—'}</div>
  </div>
);
